import type { ReactNode } from "react";
import { Table as MantineTable, Text } from "@mantine/core";

/*
 * A typed, declarative table — rendered through Mantine's `Table` +
 * `Table.ScrollContainer`. The public `Column<Row>{key,header,render,align,
 * width}` + `rowKey`/`onRowClick`/`empty` contract is shared by every screen
 * that builds columns (queue, members, users, verdict report, ticket grid,
 * preview).
 */

export interface Column<Row> {
  /** Stable key (also the default header text when `header` is omitted). */
  key: string;
  header?: ReactNode;
  /** Cell content. Receives the row. */
  render: (row: Row) => ReactNode;
  align?: "left" | "right";
  /** Fixed/preferred column width (CSS value). */
  width?: string;
}

export interface TableProps<Row> {
  columns: Array<Column<Row>>;
  rows: Row[];
  /** Stable React key per row. */
  rowKey: (row: Row) => string;
  /** Make rows interactive (keyboard + pointer). */
  onRowClick?: (row: Row) => void;
  /** Shown in place of the body when `rows` is empty. */
  empty?: ReactNode;
}

export function Table<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty = "No records.",
}: TableProps<Row>) {
  const clickable = Boolean(onRowClick);
  return (
    <MantineTable.ScrollContainer minWidth={480}>
      <MantineTable
        striped="odd"
        highlightOnHover={clickable}
        verticalSpacing="sm"
        horizontalSpacing="md"
        stickyHeader
      >
        <MantineTable.Thead>
          <MantineTable.Tr>
            {columns.map((col) => (
              <MantineTable.Th
                key={col.key}
                style={{
                  textAlign: col.align === "right" ? "right" : undefined,
                  width: col.width,
                }}
              >
                {col.header ?? col.key}
              </MantineTable.Th>
            ))}
          </MantineTable.Tr>
        </MantineTable.Thead>
        <MantineTable.Tbody>
          {rows.length === 0 ? (
            <MantineTable.Tr>
              <MantineTable.Td colSpan={columns.length}>
                <Text c="dimmed" ta="center" py="md">
                  {empty}
                </Text>
              </MantineTable.Td>
            </MantineTable.Tr>
          ) : (
            rows.map((row) => (
              <MantineTable.Tr
                key={rowKey(row)}
                tabIndex={clickable ? 0 : undefined}
                role={clickable ? "button" : undefined}
                style={clickable ? { cursor: "pointer" } : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <MantineTable.Td
                    key={col.key}
                    style={{
                      textAlign: col.align === "right" ? "right" : undefined,
                    }}
                  >
                    {col.render(row)}
                  </MantineTable.Td>
                ))}
              </MantineTable.Tr>
            ))
          )}
        </MantineTable.Tbody>
      </MantineTable>
    </MantineTable.ScrollContainer>
  );
}

/** Monospace inline cell helper for ids/timestamps. */
export function Mono({ children }: { children: ReactNode }) {
  return (
    <Text component="span" ff="monospace" fz="sm">
      {children}
    </Text>
  );
}
